import React, { Component, Fragment } from "react";
import cn from "classnames";
import "./style.css";
import { times } from "ramda";
import { line, curveMonotoneX, area } from "d3-shape";
import formatNum from "utils/formatNumber";

const years = [1990, 2016, 2030];

const width = 1200;
const height = 600;

// const formatNum = num => {
//   if (num > 1000) {
//     return `${Math.round(num / 1000)}k`;
//   }
//
//   if (num < 1) {
//     return num.toFixed(2);
//   }
//
//   return Math.round(num);
// };

const linePlot = top =>
  line()
    .x((d, n) => width / 40 * n)
    .y((d, n) => height - d / top * height)
    .curve(curveMonotoneX);

const areaPlot = top =>
  area()
    .x((d, n) => width / 40 * (n + 26))
    .y0((d, n) => height - d.base / top * height)
    .y1((d, n) => height - d.other / top * height)
    .curve(curveMonotoneX);

class Chart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
    };
  }

  componentDidMount() {
    this.setState({
      loaded: true,
    });
  }

  render() {
    const {
      title,
      per,
      data,
      top,
      good,
      bad,
      id,
      includeAxis,
      ticks = [0, 0.5, 1],
    } = this.props;
    const { loaded } = this.state;
    const plotArea = areaPlot(top);
    const plotLine = linePlot(top);

    const lastGood = good[good.length - 1];
    const lastBad = bad[bad.length - 1];

    const positions = {
      br: {
        x2: 30,
        y2: 29,
        text: {
          x: 40,
          y: 80,
          position: "start",
        },
      },
      tr: {
        x2: 30,
        y2: -29,
        text: {
          x: 40,
          y: -20,
          position: "start",
        },
      },
      r: {
        x2: 30,
        y2: -1,
        text: {
          x: 40,
          y: 30,
          position: "start",
        },
      },
      bl: {
        x2: -30,
        y2: 29,
        text: {
          x: -40,
          y: 80,
          position: "end",
        },
      },
    };

    return (
      <div
        id={id}
        className={cn("chart", { "chart--includeAxis": includeAxis })}
      >
        <div className={"chart__left"}>
          <h2 className={"chart__title"}>
            {title.split(" ").map((word, i) => (
              <Fragment>
                <span>{word}</span>
                {i !== title.split(" ").length - 1 ? " " : ""}
              </Fragment>
            ))}
            <br />
            {per.split(" ").map((word, i) => (
              <Fragment>
                <span>{word}</span>
                {i !== title.split(" ").length - 1 ? " " : ""}
              </Fragment>
            ))}
          </h2>

          {includeAxis && (
            <div className="keys">
              <h3 className="keys__title keys__fade">Key</h3>
              <p className="keys__key keys__fade">Current projection</p>
              <p className="keys__key keys__key--regression keys__fade">
                If we regress
              </p>
              <p className="keys__key keys__key--progression keys__fade">
                If we progress
              </p>
            </div>
          )}
        </div>

        <div className={"chart__right"}>
          <div className={"chart__lines"}>
            <div className={"chart__ticks"}>
              {ticks.map((n, i) => (
                <span
                  className={cn("chart__tick")}
                  style={{
                    bottom: `${n / top * 100}%`,
                  }}
                >
                  {formatNum(n)}
                </span>
              ))}
            </div>

            {data.map((n, i) => (
              <span
                className={cn("chart__line", {
                  "chart__line--highlight": i === 26,
                  "chart__line--first": i === 0,
                  "chart__line--last": i === 40,
                })}
                style={{
                  height: `${n / top * 100}%`,
                }}
              />
            ))}
            <svg className="chart__svg" viewBox={`0 0 ${width} ${height}`}>
              <defs>
                <clipPath id={`positive-mask-${id}`}>
                  <rect
                    className="chart__positive-mask"
                    x={width / 40 * 26}
                    y={0}
                    width={width / 40 * 14}
                    height={height}
                  />
                </clipPath>
                <clipPath id={`negative-mask-${id}`}>
                  <rect
                    className="chart__negative-mask"
                    x={width / 40 * 26}
                    y={0}
                    width={width / 40 * 14}
                    height={height}
                  />
                </clipPath>
                <mask id="Mask" />
              </defs>
              <path
                className="chart__positive"
                d={plotArea(
                  good.map((d, i) => ({
                    base: data[i + 26],
                    other: d,
                  }))
                )}
                clip-path={`url(#positive-mask-${id})`}
                fill="#00D5C1"
              />

              <path
                className="chart__negative"
                d={plotArea(
                  bad.map((d, i) => ({
                    base: data[i + 26],
                    other: d,
                  }))
                )}
                clip-path={`url(#negative-mask-${id})`}
                fill="#FF6371"
              />

              <path
                d={plotLine(data)}
                fill="transparent"
                stroke="#fff"
                strokeWidth="4"
                className="chart__main-line"
                style={{
                  "stroke-dasharray": loaded
                    ? this.line.getTotalLength()
                    : 1641.16,
                  "stroke-dashoffset": loaded
                    ? this.line.getTotalLength()
                    : 1641.16,
                }}
                ref={path => {
                  this.line = path;
                }}
              />

              {data.map((n, i) => {
                let direction = positions.r;

                if (i === 0) {
                  direction = positions.br;
                }

                if (i === 0 && id === "ntds") {
                  direction = positions.tr;
                }

                if (i === 26) {
                  direction = positions.bl;
                }

                const { x2, y2, text } = direction;
                return (
                  <g
                    transform={`translate(${width / 40 * i}, ${height -
                      n / top * height})`}
                    className={"circle"}
                  >
                    {(i === 0 || i === 26 || i === data.length - 1) && (
                      <g>
                        <circle
                          cx={0}
                          cy={0}
                          r={6}
                          fill="#fff"
                          stroke="#1a1c35"
                          strokeWidth="3"
                        />
                        <line
                          x1={0}
                          y1={-1}
                          x2={x2}
                          y2={y2}
                          stroke={"#fff"}
                          strokeWidth={2}
                          className="circle-line"
                        />
                        <text
                          x={text.x}
                          textAnchor={text.position}
                          y={text.y}
                          fill="#fff"
                          className="circle-text"
                        >
                          {formatNum(n)}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

              <g
                transform={`translate(${width}, ${height -
                  lastGood / top * height})`}
                className={"good-circle"}
              >
                <g>
                  <circle
                    cx={0}
                    cy={0}
                    r={6}
                    fill="#00D5C1"
                    stroke="#1a1c35"
                    strokeWidth="3"
                  />
                  <line
                    x1={0}
                    y1={-1}
                    x2={30}
                    y2={-1}
                    stroke={"#00D5C1"}
                    strokeWidth={2}
                    className="good-circle-line"
                  />
                  <text
                    x={40}
                    y={30}
                    fill="#00D5C1"
                    className="good-circle-text"
                  >
                    {formatNum(lastGood)}
                  </text>
                </g>
                )}
              </g>

              <g
                transform={`translate(${width}, ${height -
                  lastBad / top * height})`}
                className={"bad-circle"}
              >
                <g>
                  <circle
                    cx={0}
                    cy={0}
                    r={6}
                    fill="#FF6371"
                    stroke="#1a1c35"
                    strokeWidth="3"
                  />
                  <line
                    x1={0}
                    y1={-1}
                    x2={30}
                    y2={-1}
                    stroke={"#FF6371"}
                    strokeWidth={2}
                    className="bad-circle-line"
                  />
                  <text
                    x={40}
                    y={30}
                    fill="#FF6371"
                    className="bad-circle-text"
                  >
                    {formatNum(lastBad)}
                  </text>
                </g>
                )}
              </g>
            </svg>
          </div>

          {includeAxis && (
            <div className={"chart__axis"}>
              {data.map((num, i) => (
                <span
                  className={cn("chart__axis-num", {
                    "chart__axis-num--left": i === 0,
                    "chart__axis-num--right": i === data.length - 1,
                    "chart__axis-num--hidden":
                      i !== 0 && i !== 26 && i !== data.length - 1,
                  })}
                  style={{
                    left: `${100 / 40 * i}%`,
                  }}
                >
                  <span>{i + 1990}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Chart;



// WEBPACK FOOTER //
// ./src/components/chart/index.js